import { useEffect, useRef, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Chip,
  Button,
  CircularProgress,
  TextField,
  Stack,
  Divider,
  Snackbar,
  Alert,
} from "@mui/material";
import { io } from "socket.io-client";
import api from "../../utils/api";

/* ================== helpers ================== */
function formatSeconds(sec) {
  if (sec == null) return "-";
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

function statusColor(status) {
  if (status === "active") return "success";
  if (status === "idle") return "warning";
  return "error";
}

/* ================== component ================== */
export default function LiveExamControl() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [exams, setExams] = useState([]);
  const [globalLimit, setGlobalLimit] = useState(0);

  const socketRef = useRef(null);

  const [toast, setToast] = useState({
    open: false,
    message: "",
    severity: "info",
  });

  const notify = (message, severity = "info") =>
    setToast({ open: true, message, severity });

  /* ===================================================
     INITIAL REST LOAD (SOURCE OF TRUTH)
  =================================================== */
  const loadData = async () => {
    try {
      setLoading(true);

      const [examsRes, limitsRes] = await Promise.all([
        api.get("/admin/live-exams"),
        api.get("/admin/exam-limits"),
      ]);

      setExams(examsRes.data.exams || []);

 const globals = limitsRes.data.limits
  ?.filter((l) => l.scope === "global")
  .sort((a, b) => b.id - a.id);

if (globals?.length) {
  setGlobalLimit(globals[0].max_concurrent);
}

    
    } catch {
      notify("Failed to load live exam data", "error");
    } finally {
      setLoading(false);
    }
  };

  /* ===================================================
     SOCKET CONNECTION (REAL-TIME)
  =================================================== */
useEffect(() => {
  loadData();

  const token = localStorage.getItem("token");
  const sessionToken = localStorage.getItem("sessionToken");

  if (!token || !sessionToken) return;

  const socket = io(
    process.env.NODE_ENV === "production"
      ? "https://api.cbt-master.com.ng"
      : "http://localhost:4000",
    {
      auth: { token, sessionToken },
      transports: ["websocket"],
      reconnection: true,
      reconnectionAttempts: Infinity,
    }
  );

  socketRef.current = socket;

  socket.on("connect", () => {
    console.log("🔗 socket connected");
    socket.emit("admin:live-exams:subscribe");
  });

  socket.on("live-exams:update", (rows) => {
    setExams(rows || []);
  });

  socket.on("disconnect", (reason) => {
    console.log("⚠️ socket disconnected:", reason);
  });

  // 🔒 IMPORTANT: DO NOT force disconnect on every re-render
  return () => {
    if (socket.connected) {
      socket.off(); // remove listeners only
    }
  };
}, []);

  /* ===================================================
     SAVE GLOBAL LIMIT
  =================================================== */
  const saveGlobalLimit = async () => {
  if (!globalLimit || Number(globalLimit) < 1) {
    notify("Invalid slot limit", "warning");
    return;
  }

  try {
    setSaving(true);
    await api.post("/admin/exam-limits", {
      scope: "global",
      max_concurrent: Number(globalLimit),
    });

    await loadData(); // 🔥 THIS IS REQUIRED
    notify("Global exam slot limit updated", "success");
  } catch {
    notify("Failed to update slot limit", "error");
  } finally {
    setSaving(false);
  }
};

  /* ===================================================
     FORCE END EXAM
  =================================================== */
  const forceEndExam = async (exam) => {
    if (
      !window.confirm(
        `Force submit exam for ${exam.full_name}? This cannot be undone.`
      )
    )
      return;

    try {
      await api.post("/admin/force-end-exam", {
        exam_id: exam.exam_id,
        student_id: exam.student_id,
      });
      notify("Exam force-ended successfully", "success");
    } catch {
      notify("Failed to force-end exam", "error");
    }
  };

  /* ===================================================
     UI
  =================================================== */
  if (loading) {
    return (
      <Box
        height="70vh"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <CircularProgress size={50} />
      </Box>
    );
  }

  return (
    <Box>
      {/* ================= HEADER ================= */}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <Box>
          <Typography variant="h5" fontWeight={800}>
            🎛️ Live Exam Control
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Real-time monitoring of students currently taking exams
          </Typography>
        </Box>

        <Button variant="outlined" onClick={loadData}>
          Manual Refresh
        </Button>
      </Stack>

      {/* ================= SLOT CONTROL ================= */}
      <Paper sx={{ p: 2.5, mb: 3 }} variant="outlined">
        <Typography fontWeight={700} mb={1}>
          Global Exam Slot Limit
        </Typography>

        <Stack direction="row" spacing={2} alignItems="center">
<TextField
  type="number"
  label="Max concurrent exams"
  value={globalLimit}
  onChange={(e) => {
    const v = e.target.value;
    if (v === "") {
      setGlobalLimit(0);
    } else {
      setGlobalLimit(Number(v));
    }
  }}
  inputProps={{ min: 1 }}
/>


          <Button
            variant="contained"
            onClick={saveGlobalLimit}
            disabled={saving}
          >
            Save
          </Button>
        </Stack>
      </Paper>

      {/* ================= LIVE EXAMS TABLE ================= */}
      <Paper sx={{ p: 2.5 }} variant="outlined">
        <Typography fontWeight={700} mb={1}>
          Current Exam Takers ({exams.length})
        </Typography>

        <Divider sx={{ mb: 1.5 }} />

        {exams.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No active exams at the moment.
          </Typography>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Student</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Subject</TableCell>
                <TableCell>Time Left</TableCell>
                <TableCell>Last Seen</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Action</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {exams.map((ex) => (
                <TableRow key={ex.exam_id} hover>
                  <TableCell>{ex.full_name}</TableCell>
                  <TableCell>{ex.email}</TableCell>
                  <TableCell>{ex.subject}</TableCell>
                  <TableCell>
                    {formatSeconds(ex.remaining_seconds)}
                  </TableCell>
                  <TableCell>
                    {new Date(ex.last_seen).toLocaleTimeString()}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={ex.activity_status}
                      color={statusColor(ex.activity_status)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Button
                      size="small"
                      color="error"
                      variant="outlined"
                      onClick={() => forceEndExam(ex)}
                    >
                      Force End
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Paper>

      {/* ================= TOAST ================= */}
      <Snackbar
        open={toast.open}
        autoHideDuration={3500}
        onClose={() => setToast({ ...toast, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          severity={toast.severity}
          onClose={() => setToast({ ...toast, open: false })}
          variant="filled"
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
